import { formatAbbrev } from './format'

export type Axis = { min: number; max: number; ticks: number }

// Candidate tick steps (× a power of ten) and how many intervals an axis may have.
const NICE_STEPS = [1, 2, 2.5, 5]
const MIN_TICKS = 2
const MAX_TICKS = 5

/**
 * Y-axis bounds as bristol's bar chart picks them (`niceAxis`): of every
 * "nice" step giving 2-5 intervals, the one whose range wastes least space
 * beyond [lo, hi], e.g. 0-600M for a 556M max. Apex's own nice scale rounds
 * up far more aggressively.
 */
export const niceAxis = (lo: number, hi: number): Axis => {
  const range = hi - lo
  if (!(range > 0)) return { min: lo, max: lo + 1, ticks: 1 }
  const exponent = Math.floor(Math.log10(range / MAX_TICKS))
  let best: Axis | null = null
  for (let e = exponent; e <= exponent + 1; e++) {
    for (const multiple of NICE_STEPS) {
      const step = multiple * 10 ** e
      const min = Math.floor(lo / step) * step
      const max = Math.ceil(hi / step) * step
      const ticks = Math.round((max - min) / step)
      if (ticks < MIN_TICKS || ticks > MAX_TICKS) continue
      if (!best || max - min < best.max - best.min) best = { min, max, ticks }
    }
  }
  return best ?? { min: lo, max: hi, ticks: MAX_TICKS }
}

/** A tick at just the precision it needs: '600M', '4.1M', '1.25M', '0'. */
export const tickLabel = (value: number): string => {
  const label = formatAbbrev(value, null, 4)
  return label.includes('.')
    ? label.replace(/0+(?=[A-Z]?$)/, '').replace(/\.(?=[A-Z]?$)/, '')
    : label
}
